import React, { useCallback, useEffect, useState } from 'react';
import { apiDelete, apiGet, apiPost, apiPut } from '../services/api';
import { ESTILO_DO_NIVEL, FaixaAviso } from './FaixaDeAvisos';
import { TelaDePausa } from './PausaDoSistema';

/**
 * Configurações → Avisos: o administrador publica a faixa que aparece no topo
 * do sistema entre o início e o fim, para todos ou só para os perfis escolhidos.
 */

const TAMANHO_MAXIMO = 500;

// <input type="datetime-local"> trabalha no horário do aparelho, sem fuso:
// "2026-10-08T22:00". O servidor recebe ISO com fuso (UTC).
const paraCampo = (data) => {
  const d = new Date(data);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const doCampo = (valor) => (valor ? new Date(valor).toISOString() : null);
const formatar = (data) => new Date(data).toLocaleString('pt-BR', {
  day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
});

const formularioVazio = () => {
  const agora = new Date();
  agora.setSeconds(0, 0);
  const meiaNoite = new Date(agora);
  meiaNoite.setHours(24, 0, 0, 0);
  return { id: null, mensagem: '', inicio: paraCampo(agora), fim: paraCampo(meiaNoite), nivel: 'atencao', perfis: [], pausa: false };
};

const SITUACAO = {
  no_ar: { rotulo: 'No ar', classe: 'bg-[#13983D] text-white' },
  agendado: { rotulo: 'Agendado', classe: 'bg-[#3B82F6] text-white' },
  encerrado: { rotulo: 'Encerrado', classe: 'bg-slate-500/30 text-slate-400' },
};

function GerenciarAvisos({ darkMode, cardClass, titleClass, mutedClass }) {
  const [form, setForm] = useState(formularioVazio);
  const [avisos, setAvisos] = useState([]);
  const [salvando, setSalvando] = useState(false);
  const [mensagem, setMensagem] = useState(null); // { ok, texto }
  const [perfisDisponiveis, setPerfisDisponiveis] = useState([]);

  useEffect(() => {
    apiGet('/avisos/perfis/')
      .then((lista) => setPerfisDisponiveis(Array.isArray(lista) ? lista : []))
      .catch(() => {});
  }, []);

  const rotuloDosPerfis = (perfis) => (perfis && perfis.length
    ? perfis.map((p) => perfisDisponiveis.find((x) => x.value === p)?.label || p).join(', ')
    : 'Todos os perfis');

  const alternarPerfil = (valor) => setForm((f) => ({
    ...f,
    perfis: f.perfis.includes(valor) ? f.perfis.filter((p) => p !== valor) : [...f.perfis, valor],
  }));

  const carregar = useCallback(() => {
    apiGet('/avisos/')
      .then((lista) => setAvisos(Array.isArray(lista) ? lista : []))
      .catch((err) => setMensagem({ ok: false, texto: err?.message || 'Não foi possível carregar os avisos.' }));
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const campo = `w-full px-4 py-2.5 rounded-lg text-sm border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50 ${
    darkMode
      ? 'bg-slate-700/50 border-slate-600 text-slate-200 placeholder-slate-500'
      : 'bg-slate-50 border-slate-200 text-slate-700 placeholder-slate-400'
  }`;
  const botaoPequeno = `px-3 py-1.5 rounded-lg text-xs font-medium border whitespace-nowrap ${
    darkMode ? 'border-slate-600 text-slate-300 hover:bg-slate-700' : 'border-slate-200 text-slate-600 hover:bg-slate-50'
  }`;

  const publicar = async () => {
    setMensagem(null);
    if (!form.mensagem.trim()) {
      setMensagem({ ok: false, texto: 'Escreva a mensagem do aviso.' });
      return;
    }
    if (!form.inicio || !form.fim || new Date(form.fim) <= new Date(form.inicio)) {
      setMensagem({ ok: false, texto: 'O fim precisa ser depois do início.' });
      return;
    }
    const corpo = {
      mensagem: form.mensagem.trim(),
      inicio: doCampo(form.inicio),
      fim: doCampo(form.fim),
      nivel: form.nivel,
      perfis: form.perfis,
      pausa: form.pausa,
    };
    setSalvando(true);
    try {
      if (form.id) {
        await apiPut(`/avisos/${form.id}/`, corpo);
        setMensagem({ ok: true, texto: 'Aviso atualizado.' });
      } else {
        await apiPost('/avisos/', corpo);
        setMensagem({ ok: true, texto: corpo.pausa
          ? 'Pausa agendada. No horário, só administradores conseguem usar o sistema.'
          : 'Aviso publicado. Ele aparece para todos dentro do período escolhido.' });
      }
      setForm(formularioVazio());
      carregar();
    } catch (err) {
      setMensagem({ ok: false, texto: err?.message || 'Não foi possível salvar o aviso.' });
    } finally {
      setSalvando(false);
    }
  };

  const editar = (a) => {
    setMensagem(null);
    setForm({
      id: a.id, mensagem: a.mensagem, inicio: paraCampo(a.inicio), fim: paraCampo(a.fim),
      nivel: a.nivel, perfis: a.perfis || [], pausa: !!a.pausa,
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const encerrar = async (a) => {
    try {
      await apiPost(`/avisos/${a.id}/encerrar/`, {});
      carregar();
    } catch (err) {
      setMensagem({ ok: false, texto: err?.message || 'Não foi possível encerrar o aviso.' });
    }
  };

  const excluir = async (a) => {
    if (!window.confirm('Excluir este aviso?')) return;
    try {
      await apiDelete(`/avisos/${a.id}/`);
      if (form.id === a.id) setForm(formularioVazio());
      carregar();
    } catch (err) {
      setMensagem({ ok: false, texto: err?.message || 'Não foi possível excluir o aviso.' });
    }
  };

  return (
    <div className="space-y-6">
      <div className={cardClass}>
        <h2 className={titleClass}>{form.id ? 'Editar aviso' : 'Novo aviso'}</h2>
        <p className={`text-sm mb-4 ${mutedClass}`}>
          A faixa aparece no topo do sistema, para os perfis escolhidos, entre o início e o
          fim. Depois do fim ela some sozinha.
        </p>

        <label className={`block text-sm mb-1 ${mutedClass}`} htmlFor="aviso-mensagem">Mensagem</label>
        <textarea
          id="aviso-mensagem"
          rows={3}
          maxLength={TAMANHO_MAXIMO}
          value={form.mensagem}
          onChange={(e) => setForm((f) => ({ ...f, mensagem: e.target.value }))}
          placeholder="Ex.: O SIDEB ficará fora do ar hoje, das 22h às 00h, para manutenção. Salve seus lançamentos antes desse horário."
          className={campo}
        />
        <div className={`text-xs text-right mt-1 ${mutedClass}`}>{form.mensagem.length}/{TAMANHO_MAXIMO}</div>

        <div className="flex flex-wrap gap-4 mt-2">
          <div>
            <label className={`block text-sm mb-1 ${mutedClass}`} htmlFor="aviso-inicio">{form.pausa ? 'Pausa começa' : 'Mostrar a partir de'}</label>
            <input id="aviso-inicio" type="datetime-local" value={form.inicio}
              onChange={(e) => setForm((f) => ({ ...f, inicio: e.target.value }))} className={campo} />
          </div>
          <div>
            <label className={`block text-sm mb-1 ${mutedClass}`} htmlFor="aviso-fim">{form.pausa ? 'Sistema volta' : 'Mostrar até'}</label>
            <input id="aviso-fim" type="datetime-local" value={form.fim}
              onChange={(e) => setForm((f) => ({ ...f, fim: e.target.value }))} className={campo} />
          </div>
          {!form.pausa && (
          <div>
            <span className={`block text-sm mb-1 ${mutedClass}`}>Cor</span>
            <div className="flex gap-2">
              {Object.entries(ESTILO_DO_NIVEL).map(([nivel, estilo]) => {
                const ativo = form.nivel === nivel;
                return (
                  <button key={nivel} type="button"
                    onClick={() => setForm((f) => ({ ...f, nivel }))}
                    className={`px-3 py-2.5 rounded-lg text-sm font-medium border ${ativo ? 'border-transparent' : darkMode ? 'border-slate-600 text-slate-300' : 'border-slate-200 text-slate-600'}`}
                    style={ativo ? { background: estilo.fundo, color: estilo.texto } : {}}
                    aria-pressed={ativo}>
                    {estilo.rotulo}
                  </button>
                );
              })}
            </div>
          </div>
          )}
        </div>

        <div className="mt-4">
          <span className={`block text-sm mb-1 ${mutedClass}`}>Quem recebe o aviso</span>
          <div className="flex flex-wrap gap-2">
            {(() => {
              const todos = form.perfis.length === 0;
              const chip = (ativo) => `px-3 py-2 rounded-lg text-sm font-medium border ${ativo
                ? 'bg-[#0974BA] border-transparent text-white'
                : darkMode ? 'border-slate-600 text-slate-300 hover:border-slate-500' : 'border-slate-200 text-slate-600 hover:border-slate-300'}`;
              return (
                <>
                  <button type="button" className={chip(todos)} aria-pressed={todos}
                    onClick={() => setForm((f) => ({ ...f, perfis: [] }))}>
                    Todos os perfis
                  </button>
                  {perfisDisponiveis.map((p) => {
                    const ativo = form.perfis.includes(p.value);
                    return (
                      <button key={p.value} type="button" className={chip(ativo)} aria-pressed={ativo}
                        onClick={() => alternarPerfil(p.value)}>
                        {ativo ? '✓ ' : ''}{p.label}
                      </button>
                    );
                  })}
                </>
              );
            })()}
          </div>
          <p className={`text-xs mt-1.5 ${mutedClass}`}>
            {form.perfis.length
              ? `Só ${rotuloDosPerfis(form.perfis)} vão ver o aviso.`
              : 'Todas as contas vão ver o aviso. Marque um ou mais perfis para restringir.'}
          </p>
        </div>

        <label
          htmlFor="aviso-pausa"
          className={`mt-5 flex items-start gap-3 rounded-xl border p-4 cursor-pointer ${form.pausa
            ? 'border-[#E83A3C]/60 bg-[#E83A3C]/10'
            : darkMode ? 'border-slate-600' : 'border-slate-200'}`}
        >
          <input
            id="aviso-pausa"
            type="checkbox"
            checked={form.pausa}
            onChange={(e) => setForm((f) => ({ ...f, pausa: e.target.checked }))}
            className="mt-0.5 h-5 w-5 accent-[#E83A3C] flex-shrink-0"
          />
          <span>
            <span className={`block text-sm font-semibold ${form.pausa ? 'text-[#E83A3C]' : ''}`}>
              Pausar o sistema neste período
            </span>
            <span className={`block text-xs mt-0.5 ${mutedClass}`}>
              Entre o início e o fim, {form.perfis.length
                ? `os perfis ${rotuloDosPerfis(form.perfis)} não conseguem`
                : 'ninguém consegue'} usar o sistema: quem está dentro vê a tela "Sistema em pausa" com esta mensagem e a hora de
              volta, e o login fica bloqueado. Só administradores continuam entrando. Nas 24 horas antes, todos
              recebem o aviso de quando a pausa começa. "Encerrar agora" libera na hora.
            </span>
          </span>
        </label>

        <div className={`mt-5 rounded-xl border border-dashed p-3 ${darkMode ? 'border-slate-600' : 'border-slate-300'}`}>
          <div className={`text-xs mb-2 ${mutedClass}`}>Prévia de como as pessoas vão ver:</div>
          {form.pausa ? (
            <div className="rounded-xl overflow-hidden" style={{ height: 300 }}>
              <div style={{ transform: 'scale(0.62)', transformOrigin: 'top left', width: '161.3%', height: 484 }} className="relative">
                <TelaDePausa
                  pausa={{ mensagem: form.mensagem || 'Sua mensagem aparece aqui.', fim: doCampo(form.fim) || new Date().toISOString() }}
                  agora={new Date(form.inicio || Date.now()).getTime()}
                  onSair={() => {}}
                  darkMode={darkMode}
                  embutida
                />
              </div>
            </div>
          ) : (
            <FaixaAviso aviso={{ mensagem: form.mensagem || 'Sua mensagem aparece aqui.', nivel: form.nivel }} />
          )}
        </div>

        {mensagem && (
          <div className={`mt-4 p-3 rounded-lg text-sm border ${mensagem.ok
            ? 'text-[#13983D] border-[#13983D]/40 bg-[#13983D]/10'
            : 'text-[#E83A3C] border-[#E83A3C]/40 bg-[#E83A3C]/10'}`}>
            {mensagem.texto}
          </div>
        )}

        <div className="flex gap-3 mt-5">
          <button type="button" onClick={publicar} disabled={salvando}
            className="px-6 py-2.5 rounded-lg font-medium text-sm text-white bg-[#0974BA] hover:bg-[#0974BA]/80 disabled:opacity-60">
            {salvando ? 'Salvando...' : form.id ? 'Salvar alterações' : 'Publicar aviso'}
          </button>
          {form.id && (
            <button type="button" onClick={() => { setForm(formularioVazio()); setMensagem(null); }}
              className={`px-4 py-2.5 rounded-lg text-sm border ${darkMode ? 'border-slate-600 text-slate-300' : 'border-slate-200 text-slate-600'}`}>
              Cancelar edição
            </button>
          )}
        </div>
      </div>

      <div className={cardClass}>
        <h2 className={titleClass}>Avisos</h2>
        {avisos.length === 0 ? (
          <p className={`text-sm ${mutedClass}`}>Nenhum aviso publicado.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className={`text-left text-xs ${mutedClass}`}>
                  <th className="py-2 pr-3 font-semibold">Mensagem</th>
                  <th className="py-2 pr-3 font-semibold">Para</th>
                  <th className="py-2 pr-3 font-semibold">Período</th>
                  <th className="py-2 pr-3 font-semibold">Situação</th>
                  <th className="py-2" />
                </tr>
              </thead>
              <tbody>
                {avisos.map((a) => {
                  const s = SITUACAO[a.situacao] || SITUACAO.encerrado;
                  const encerrado = a.situacao === 'encerrado';
                  return (
                    <tr key={a.id} className={`border-t align-top ${darkMode ? 'border-slate-700/60' : 'border-slate-200'} ${encerrado ? 'opacity-60' : ''}`}>
                      <td className="py-3 pr-3 min-w-[240px]">
                        <div className="line-clamp-2">{a.mensagem}</div>
                        {a.criado_por_nome && <div className={`text-xs mt-1 ${mutedClass}`}>por {a.criado_por_nome}</div>}
                      </td>
                      <td className="py-3 pr-3 min-w-[140px]">{rotuloDosPerfis(a.perfis)}</td>
                      <td className="py-3 pr-3 whitespace-nowrap">{formatar(a.inicio)} → {formatar(a.fim)}</td>
                      <td className="py-3 pr-3">
                        <div className="flex flex-wrap gap-1.5">
                          <span className={`text-xs font-bold rounded-full px-2.5 py-1 whitespace-nowrap ${s.classe}`}>{s.rotulo}</span>
                          {a.pausa && (
                            <span className="text-xs font-bold rounded-full px-2.5 py-1 whitespace-nowrap bg-[#E83A3C] text-white">⏸ Pausa</span>
                          )}
                        </div>
                      </td>
                      <td className="py-3 text-right whitespace-nowrap space-x-2">
                        {!encerrado && <button type="button" className={botaoPequeno} onClick={() => editar(a)}>Editar</button>}
                        {a.situacao === 'no_ar' && <button type="button" className={botaoPequeno} onClick={() => encerrar(a)}>Encerrar agora</button>}
                        <button type="button" className={botaoPequeno} onClick={() => excluir(a)}>Excluir</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default GerenciarAvisos;
